import React, { useEffect, useState } from 'react';
import { Fingerprint, Check } from 'lucide-react';
import {
  isBiometricAvailable,
  isBiometricEnabled,
  enableBiometricLogin,
  disableBiometricLogin,
} from '../lib/biometric';
import { isNative } from '../lib/platform';

/**
 * Only rendered inside the native mobile shell. On the web build the whole
 * component returns null so no dead UI leaks into browser users.
 *
 * The toggle stores the current session token in the OS keychain gated
 * behind biometrics. On next launch the app can restore the session with
 * a Face ID / fingerprint prompt (see LoginPage integration).
 */
export default function BiometricToggle({ userEmail }) {
  const [available, setAvailable] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [biometryLabel, setBiometryLabel] = useState('Biometrics');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!isNative()) return;
    (async () => {
      const a = await isBiometricAvailable();
      setAvailable(!!a.available);
      if (a.face) setBiometryLabel('Face ID');
      else if (a.fingerprint) setBiometryLabel('Fingerprint');
      const e = await isBiometricEnabled();
      setEnabled(e);
    })();
  }, []);

  if (!isNative() || !available) return null;

  const toggle = async () => {
    setBusy(true);
    setMessage('');
    try {
      if (enabled) {
        await disableBiometricLogin();
        setEnabled(false);
        setMessage('Biometric login disabled.');
      } else {
        const sessionToken = localStorage.getItem('connectify_session_token');
        if (!sessionToken || !userEmail) {
          setMessage('Please sign in again first, then enable biometrics.');
          return;
        }
        const r = await enableBiometricLogin({ email: userEmail, sessionToken });
        if (r.ok) {
          setEnabled(true);
          setMessage(`${biometryLabel} unlock is now on.`);
        } else {
          setMessage('Could not enable biometrics. Try again from Settings.');
        }
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 sm:p-8 shadow-sm border border-gray-100 dark:border-neutral-800">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-gray-100 dark:bg-neutral-800 rounded-full flex items-center justify-center">
          <Fingerprint size={20} className="text-gray-600 dark:text-neutral-400" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100">
          {biometryLabel} Unlock
        </h2>
      </div>

      <p className="text-gray-600 dark:text-neutral-400 text-sm mb-6">
        Sign in with {biometryLabel} the next time you open the app. Your session
        token is stored in your device's secure keychain and never leaves this device.
      </p>

      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        data-testid="biometric-toggle-btn"
        className={`w-full py-3 rounded-xl font-bold transition-colors ${
          enabled
            ? 'bg-gray-100 dark:bg-neutral-800 text-gray-900 dark:text-neutral-100 hover:bg-gray-200 dark:hover:bg-neutral-700'
            : 'bg-black text-white hover:bg-gray-800 dark:border-2 dark:border-white'
        }`}
      >
        {enabled ? (
          <span className="inline-flex items-center gap-2 justify-center">
            <Check size={18} /> {biometryLabel} enabled — tap to disable
          </span>
        ) : (
          `Enable ${biometryLabel} unlock`
        )}
      </button>

      {message && (
        <p className="text-sm text-gray-600 dark:text-neutral-400 mt-4 text-center">
          {message}
        </p>
      )}
    </div>
  );
}
